/**
 * Visoes rapidas: atalhos para as perguntas que cada publico faz todo dia
 * ("o que esta em coleta?", "quais potros estao sem avaliacao?"). Uma visao e so
 * um link com os filtros ja na URL: a pagina de destino continua sendo a fonte
 * de verdade, e voltar, compartilhar ou recarregar funcionam sem estado extra.
 *
 * Fica fora dos componentes (sem React) para a regra de "qual esta ativa" ter
 * teste proprio.
 */

/** Quem esta usando: equipe que cadastra, avaliador so consultando (demo) ou dono do haras. */
export type PublicoVisao = 'equipe' | 'consulta' | 'dono';

export type IconeVisao = 'relogio' | 'selo' | 'alerta' | 'alvo' | 'broto' | 'etiqueta';

export interface Visao {
  id: string;
  rotulo: string;
  icone: IconeVisao;
  /** Frase completa, para a dica (o rotulo precisa caber em 200px). */
  descricao: string;
  /** Caminho com a busca, ex.: '/animais?classe=potro'. */
  para: string;
}

const EM_COLETA: Visao = {
  id: 'em-coleta',
  rotulo: 'Fichas em coleta',
  descricao: 'Fichas ainda abertas (em coleta ou reabertas)',
  icone: 'relogio',
  para: '/dados?situacao=abertas',
};
const POR_VALIDAR: Visao = {
  id: 'por-validar',
  rotulo: 'Fichas por validar',
  descricao: 'Fichas fechadas que ainda não foram validadas',
  icone: 'selo',
  para: '/dados?situacao=fechadas',
};
const POTROS_VENCIDOS: Visao = {
  id: 'potros-vencidos',
  rotulo: 'Potros sem avaliação',
  descricao: 'Potros sem nenhuma avaliação nos últimos 12 meses',
  icone: 'alerta',
  para: '/animais?classe=potro&avaliacao=pendente',
};
const REPETIBILIDADE: Visao = {
  id: 'repetibilidade',
  rotulo: 'Repetibilidade',
  descricao: 'Relatório de repetibilidade das medidas (ICC)',
  icone: 'alvo',
  para: '/repetibilidade',
};
const RECENTES: Visao = {
  id: 'recentes',
  rotulo: 'Avaliados por último',
  descricao: 'Plantel ordenado pela avaliação mais recente',
  icone: 'selo',
  para: '/animais?ordem=avaliacao&dir=desc',
};

const VISOES: Record<PublicoVisao, readonly Visao[]> = {
  equipe: [EM_COLETA, POR_VALIDAR, POTROS_VENCIDOS, REPETIBILIDADE],
  // Avaliador sem cadastro (dados ficticios): ve Dados e Plantel, nao coleta.
  consulta: [POR_VALIDAR, POTROS_VENCIDOS, RECENTES, REPETIBILIDADE],
  dono: [
    RECENTES,
    {
      id: 'potros',
      rotulo: 'Potros',
      descricao: 'Só os potros do plantel',
      icone: 'broto',
      para: '/animais?classe=potro',
    },
    {
      id: 'eguas',
      rotulo: 'Éguas',
      descricao: 'Só as éguas do plantel',
      icone: 'etiqueta',
      para: '/animais?classe=egua',
    },
    {
      id: 'garanhoes',
      rotulo: 'Garanhões',
      descricao: 'Só os garanhões do plantel',
      icone: 'etiqueta',
      para: '/animais?classe=garanhao',
    },
  ],
};

export function visoesPara(publico: PublicoVisao): readonly Visao[] {
  return VISOES[publico];
}

/**
 * A visao esta ativa quando o caminho e o MESMO conjunto de filtros batem.
 * Igualdade exata, e nao "contem": senao "Potros" acenderia junto com "Potros
 * sem avaliacao", e um filtro a mais escolhido a mao deixaria de ser a visao.
 * A ordem dos parametros na URL nao importa.
 */
export function visaoAtiva(visao: Visao, local: { pathname: string; search: string }): boolean {
  const alvo = new URL(visao.para, 'http://x');
  if (alvo.pathname !== local.pathname) return false;
  const chave = (busca: URLSearchParams) =>
    [...busca.entries()]
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${k}=${v}`)
      .sort()
      .join('&');
  return chave(alvo.searchParams) === chave(new URLSearchParams(local.search));
}
